import type { TFuncKey } from 'i18next';
import * as React from 'react';
import { type HTMLProps } from 'react';
import { useDebouncedLoader } from 'use-debounced-loader';

import { Button as UiButton } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DynamicT from '@/shared/components/DynamicT';

import RotatingRingIcon from './RotatingRingIcon';

export type ButtonType = 'primary' | 'secondary' | 'danger';

const typeToVariant: Record<ButtonType, 'default' | 'outline' | 'destructive'> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
};

const sizeToUiSize: Record<'small' | 'large', 'sm' | 'lg'> = {
  small: 'sm',
  large: 'lg',
};

const sizeToClassName: Record<'small' | 'large', string> = {
  small: 'min-w-[85px]',
  large: 'w-full',
};

type BaseProps = Omit<HTMLProps<HTMLButtonElement>, 'type' | 'size' | 'title'> & {
  readonly htmlType?: 'button' | 'submit' | 'reset';
  readonly type?: ButtonType;
  readonly size?: 'small' | 'large';
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly onClick?: React.MouseEventHandler;
};

type Props = BaseProps & {
  readonly title: TFuncKey;
  readonly icon?: React.ReactNode;
  readonly i18nProps?: Record<string, string>;
};

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      htmlType = 'button',
      type = 'primary',
      size = 'large',
      title,
      i18nProps,
      className,
      isDisabled = false,
      isLoading = false,
      icon,
      onClick,
      ...rest
    },
    ref
  ) => {
    const isLoadingActive = useDebouncedLoader(isLoading, 300);

    return (
      <UiButton
        ref={ref}
        type={htmlType}
        variant={typeToVariant[type]}
        size={sizeToUiSize[size]}
        disabled={isDisabled || isLoading}
        className={cn(sizeToClassName[size], 'py-5', className)}
        onClick={onClick}
        {...rest}
      >
        {isLoadingActive ? <RotatingRingIcon /> : icon}
        <DynamicT forKey={title} interpolation={i18nProps} />
      </UiButton>
    );
  }
);
Button.displayName = 'Button';

export default Button;
